import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, XCircle, Plus, Minus, Trash2, X, AlertTriangle, Truck, MessageCircle, Mail, Send, Gift, MapPin } from "lucide-react";
import api, { fmtJMD, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUS = ["pending", "processing", "processing_complete", "in_transit", "delivered", "cancelled"];
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  processing_complete: "bg-purple-100 text-purple-700",
  in_transit: "bg-cyan-100 text-cyan-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const EDITABLE_STATUSES = ["pending", "processing", "processing_complete"];

export default function AdminOrders() {
  const { isSuperAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [bulkWipe, setBulkWipe] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [feeEditing, setFeeEditing] = useState(null);  // {order, fee, note}
  const [notifying, setNotifying] = useState(null);    // {order, links}
  const [pickupEditing, setPickupEditing] = useState(null);  // {order, date, note}

  const load = async () => setOrders((await api.get("/orders")).data);
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    await api.patch(`/orders/${id}/status`, null, { params: { status } });
    toast.success(`Status: ${status}`);
    load();
  };

  const saveFee = async () => {
    if (!feeEditing) return;
    try {
      await api.patch(`/orders/${feeEditing.order.id}/delivery-fee`, {
        delivery_fee: Number(feeEditing.fee) || 0,
        note: feeEditing.note || "",
      });
      toast.success("Delivery fee updated");
      setFeeEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openNotify = async (order) => {
    try {
      const { data } = await api.get(`/orders/${order.id}/notify-link`);
      setNotifying({ order, ...data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const savePickup = async () => {
    if (!pickupEditing) return;
    try {
      await api.patch(`/orders/${pickupEditing.order.id}/pickup-date`, {
        pickup_date: pickupEditing.date,
        note: pickupEditing.note || "",
      });
      toast.success("Pickup day saved");
      setPickupEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/orders/${editing.id}`, {
        customer_name: editing.customer_name,
        customer_phone: editing.customer_phone,
        address_line: editing.address_line,
        parish: editing.parish,
        delivery_day: editing.delivery_day,
        delivery_notes: editing.delivery_notes,
        items: editing.items,
      });
      toast.success("Order updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const doCancel = async () => {
    try {
      await api.post(`/orders/${cancelling.id}/cancel`, { reason });
      toast.success("Order cancelled — stock restored");
      setCancelling(null);
      setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/orders/${deleting.id}`);
      toast.success("Order permanently deleted");
      setDeleting(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const doBulkWipe = async () => {
    if (bulkConfirm !== "WIPE_ORDERS") {
      toast.error('Type WIPE_ORDERS exactly to confirm');
      return;
    }
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/maintenance/reset-orders", { confirm: "WIPE_ORDERS" });
      toast.success(`Deleted ${r.data.deleted_count} order${r.data.deleted_count === 1 ? "" : "s"}`);
      setBulkWipe(false);
      setBulkConfirm("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-zinc-500">Orders</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Order management</h1>
          <p className="text-sm text-zinc-500 mt-1">{orders.length} order{orders.length === 1 ? "" : "s"}.</p>
        </div>
        {isSuperAdmin && orders.length > 0 && (
          <button onClick={() => setBulkWipe(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 text-sm font-semibold"
                  data-testid="wipe-all-orders-btn">
            <AlertTriangle size={14} /> Wipe all orders
          </button>
        )}
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead className="bg-zinc-50 text-zinc-500 text-xs">
            <tr>
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Parish</th>
              <th className="text-left px-4 py-3">Day</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <React.Fragment key={o.id}>
                <tr className="border-t border-zinc-100 cursor-pointer hover:bg-zinc-50" onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">{o.customer_name}</td>
                  <td className="px-4 py-3">{o.parish}</td>
                  <td className="px-4 py-3">{o.delivery_day}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtJMD(o.total)}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLOR[o.status] || "bg-zinc-100"}`}>{o.status}</span>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="border-t border-zinc-100 bg-zinc-50">
                    <td colSpan={6} className="px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                        <div>
                          <div className="overline text-zinc-500 mb-1">Contact</div>
                          <div>{o.customer_email}</div><div>{o.customer_phone}</div>
                        </div>
                        <div>
                          <div className="overline text-zinc-500 mb-1">Address</div>
                          <div>{o.address_line}</div><div>{o.parish}</div>
                          {o.notes && <div className="text-zinc-500 mt-1">Note: {o.notes}</div>}
                          {o.delivery_notes && <div className="text-amber-700 mt-1 font-semibold">Driver: {o.delivery_notes}</div>}
                          {o.coupon_code && <div className="text-green-700 mt-1">Coupon: {o.coupon_code} (-{fmtJMD(o.discount_amount || 0)})</div>}
                          {o.cancelled_reason && <div className="text-red-700 mt-1">Cancelled: {o.cancelled_reason}</div>}
                        </div>
                        <div>
                          <div className="overline text-zinc-500 mb-1">Change status</div>
                          <select value={o.status} onChange={(e) => update(o.id, e.target.value)} className="border border-zinc-200 rounded-lg px-2 py-1.5 text-sm w-full" data-testid={`order-status-${o.id}`}>
                            {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <a href={`/order/${o.id}`} target="_blank" rel="noreferrer"
                               className="text-xs font-semibold inline-flex items-center gap-1 text-[#F74B64] hover:underline"
                               data-testid={`order-print-${o.id}`}>
                              Print receipt with QR →
                            </a>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                            {(o.fulfillment_method === "pickup") ? (
                              <button onClick={() => setPickupEditing({ order: o, date: o.pickup_date || "", note: "" })}
                                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#009A49]/10 text-[#009A49] hover:bg-[#009A49]/20"
                                      data-testid={`order-pickup-${o.id}`}>
                                <MapPin size={10} /> {o.pickup_date ? "Change pickup day" : "Set pickup day"}
                              </button>
                            ) : (
                              <button onClick={() => setFeeEditing({ order: o, fee: o.delivery_fee || 0, note: "" })}
                                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#009A49]/10 text-[#009A49] hover:bg-[#009A49]/20"
                                      data-testid={`order-fee-${o.id}`}>
                                <Truck size={10} /> Set delivery fee
                              </button>
                            )}
                            <button onClick={() => openNotify(o)}
                                    className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-900 text-white hover:bg-zinc-700"
                                    data-testid={`order-notify-${o.id}`}>
                              <Send size={10} /> Notify customer
                            </button>
                            {EDITABLE_STATUSES.includes(o.status) && (
                              <button onClick={() => setEditing({ ...o })}
                                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-100 hover:bg-zinc-200"
                                      data-testid={`order-edit-${o.id}`}>
                                <Pencil size={10} /> Edit
                              </button>
                            )}
                            {o.status !== "cancelled" && o.status !== "delivered" && (
                              <button onClick={() => { setCancelling(o); setReason(""); }}
                                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                                      data-testid={`order-cancel-${o.id}`}>
                                <XCircle size={10} /> Cancel
                              </button>
                            )}
                            {isSuperAdmin && (
                              <button onClick={() => setDeleting(o)}
                                      className="text-xs font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 text-white hover:bg-red-700"
                                      title="Permanently delete this order"
                                      data-testid={`order-delete-${o.id}`}>
                                <Trash2 size={10} /> Delete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="overline text-zinc-500 mb-2">Items</div>
                        <ul className="space-y-1">
                          {o.items.map((it, i) => (
                            <li key={i} className="flex justify-between">
                              <span>{it.quantity}× {it.name}{it.custom_flavours?.length ? ` — ${it.custom_flavours.join(", ")}` : ""}</span>
                              <span className="font-semibold">{fmtJMD(it.price * it.quantity)}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 flex justify-between text-xs text-zinc-500 border-t border-zinc-200 pt-2">
                          <span>Delivery fee</span><span data-testid={`order-fee-display-${o.id}`}>{fmtJMD(o.delivery_fee)}</span>
                        </div>
                      </div>

                      {/* Recipient (gift) panel */}
                      {o.is_gift && (
                        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid={`order-gift-${o.id}`}>
                          <div className="inline-flex items-center gap-1.5 text-amber-700 font-semibold text-xs mb-1">
                            <Gift size={11} /> Gift order — deliver to recipient
                          </div>
                          <div className="text-xs text-zinc-700 leading-relaxed">
                            <div><strong>{o.recipient_name}</strong></div>
                            {o.recipient_phone && <div>{o.recipient_phone}</div>}
                            {o.recipient_email && <div>{o.recipient_email}</div>}
                            {o.recipient_address && <div>{o.recipient_address}</div>}
                          </div>
                        </div>
                      )}

                      {/* Pickup panel */}
                      {o.fulfillment_method === "pickup" && (
                        <div className="mt-3 rounded-xl bg-[#009A49]/10 border border-[#009A49]/30 p-3" data-testid={`order-pickup-panel-${o.id}`}>
                          <div className="inline-flex items-center gap-1.5 text-[#046531] font-semibold text-xs mb-1">
                            <MapPin size={11} /> Pickup — Greater Portmore, St. Catherine
                          </div>
                          <div className="text-xs text-zinc-700 leading-relaxed">
                            {o.pickup_date
                              ? <>Scheduled for <strong>{o.pickup_date}</strong></>
                              : <span className="italic text-zinc-500">No pickup day set yet — click “Set pickup day”.</span>}
                          </div>
                        </div>
                      )}

                      {/* Fee history */}
                      {(o.delivery_fee_history || []).length > 0 && (
                        <div className="mt-3 rounded-xl bg-zinc-50 border border-zinc-200 p-3 text-xs">
                          <div className="overline text-zinc-500 mb-1.5">Delivery fee history</div>
                          <ul className="space-y-1">
                            {o.delivery_fee_history.map((h, i) => (
                              <li key={i} className="flex flex-wrap gap-x-2 gap-y-0.5">
                                <span className="text-zinc-500">{(h.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                                <span>{fmtJMD(h.previous_fee)} → <strong>{fmtJMD(h.new_fee)}</strong></span>
                                <span className="text-zinc-500">by {h.updated_by_name || "admin"}</span>
                                {h.note && <span className="w-full text-zinc-600 italic">“{h.note}”</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">No orders yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg">Edit order #{editing.id.slice(0, 8)}</div>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Customer name">
                <input className="input" value={editing.customer_name || ""}
                       onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} />
              </F>
              <F label="Phone">
                <input className="input" value={editing.customer_phone || ""}
                       onChange={(e) => setEditing({ ...editing, customer_phone: e.target.value })} />
              </F>
              <F label="Address" span={2}>
                <input className="input" value={editing.address_line || ""}
                       onChange={(e) => setEditing({ ...editing, address_line: e.target.value })} />
              </F>
              <F label="Parish">
                <select className="input" value={editing.parish}
                        onChange={(e) => setEditing({ ...editing, parish: e.target.value })}>
                  <option>Kingston</option><option>Saint Catherine</option>
                </select>
              </F>
              <F label="Delivery day">
                <input type="date" className="input" value={editing.delivery_day}
                       onChange={(e) => setEditing({ ...editing, delivery_day: e.target.value })} />
              </F>
              <F label="Delivery notes" span={2}>
                <textarea rows={2} className="input" value={editing.delivery_notes || ""}
                          onChange={(e) => setEditing({ ...editing, delivery_notes: e.target.value })} />
              </F>
            </div>

            <div className="mt-5">
              <div className="overline text-zinc-500 mb-2">Items</div>
              <div className="space-y-2">
                {(editing.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-zinc-200">
                    <div className="flex-1 min-w-0 text-sm truncate">{it.name}</div>
                    <div className="flex items-center gap-1 border border-zinc-200 rounded-full px-1">
                      <button type="button"
                              onClick={() => {
                                const next = [...editing.items];
                                next[i] = { ...it, quantity: Math.max(1, (it.quantity || 1) - 1) };
                                setEditing({ ...editing, items: next });
                              }}
                              className="p-1"><Minus size={10} /></button>
                      <span className="w-5 text-center text-xs font-semibold">{it.quantity || 1}</span>
                      <button type="button"
                              onClick={() => {
                                const next = [...editing.items];
                                next[i] = { ...it, quantity: (it.quantity || 1) + 1 };
                                setEditing({ ...editing, items: next });
                              }}
                              className="p-1"><Plus size={10} /></button>
                    </div>
                    <div className="text-sm font-semibold w-20 text-right">{fmtJMD((it.price || 0) * (it.quantity || 1))}</div>
                    <button type="button" onClick={() => {
                              const next = editing.items.filter((_, j) => j !== i);
                              setEditing({ ...editing, items: next });
                            }} className="p-1 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveEdit} className="btn-primary" data-testid="save-order-edit-btn">Save changes</button>
            </div>
          </div>
        </div>
      )}

      {cancelling && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCancelling(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg">Cancel order #{cancelling.id.slice(0, 8)}?</div>
              <button onClick={() => setCancelling(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-500 mb-3">
              Stock for the items in this order will be restored automatically.
            </p>
            <textarea rows={3} className="input" placeholder="Reason (visible to the customer)"
                      value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="cancel-reason" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setCancelling(null)} className="btn-ghost">Keep order</button>
              <button onClick={doCancel}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600"
                      data-testid="confirm-cancel-btn">
                <XCircle size={14} /> Cancel order
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDeleting(null)} data-testid="delete-order-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} /> Delete permanently?
              </div>
              <button onClick={() => setDeleting(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-700 mb-2">
              Order <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">#{deleting.id.slice(0, 8)}</code>
              {" "}for <strong>{deleting.customer_name}</strong> — {fmtJMD(deleting.total)}.
            </p>
            <p className="text-sm text-zinc-500 mb-4">
              This cannot be undone. Stock is <em>not</em> restored (use Cancel for that). Use Delete only for test data or duplicates.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleting(null)} className="btn-ghost">Keep order</button>
              <button onClick={doDelete}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700"
                      data-testid="confirm-delete-btn">
                <Trash2 size={14} /> Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkWipe && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setBulkWipe(false)} data-testid="wipe-all-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg flex items-center gap-2">
                <AlertTriangle className="text-red-500" size={20} /> Wipe all orders?
              </div>
              <button onClick={() => setBulkWipe(false)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-700 mb-3">
              This will permanently delete <strong>all {orders.length}</strong> order{orders.length === 1 ? "" : "s"} in the database.
              Customer accounts, reviews, products and analytics logs are <strong>not</strong> touched.
            </p>
            <p className="text-xs text-zinc-500 mb-3">
              Use this when switching from test data to going live.
            </p>
            <label className="text-xs font-semibold text-zinc-600 block mb-1">
              Type <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px]">WIPE_ORDERS</code> to confirm
            </label>
            <input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)}
                   placeholder="WIPE_ORDERS"
                   className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono"
                   data-testid="wipe-confirm-input" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setBulkWipe(false); setBulkConfirm(""); }} className="btn-ghost">Cancel</button>
              <button onClick={doBulkWipe} disabled={bulkBusy || bulkConfirm !== "WIPE_ORDERS"}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="confirm-wipe-all-btn">
                <Trash2 size={14} /> {bulkBusy ? "Wiping…" : "Delete all orders"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery fee modal */}
      {feeEditing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setFeeEditing(null)} data-testid="fee-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg inline-flex items-center gap-2">
                <Truck size={18} className="text-[#009A49]" /> Set delivery fee
              </div>
              <button onClick={() => setFeeEditing(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              Order <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">#{feeEditing.order.id.slice(0, 8)}</code>
              {" "}for <strong>{feeEditing.order.customer_name}</strong>.
              Subtotal is {fmtJMD(feeEditing.order.subtotal)} — the new total will recalculate automatically.
            </p>
            <label className="text-xs font-semibold text-zinc-600 block mb-1">Delivery fee (JMD)</label>
            <input type="number" min="0" step="50" value={feeEditing.fee}
                   onChange={(e) => setFeeEditing({ ...feeEditing, fee: e.target.value })}
                   className="input" data-testid="fee-input" />
            <label className="text-xs font-semibold text-zinc-600 block mt-3 mb-1">Internal note (optional)</label>
            <input value={feeEditing.note}
                   onChange={(e) => setFeeEditing({ ...feeEditing, note: e.target.value })}
                   className="input" placeholder="e.g. Liguanea pickup, custom route"
                   data-testid="fee-note" />
            <div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-3 text-sm flex justify-between">
              <span>New total</span>
              <strong>{fmtJMD(
                Math.max(0, (feeEditing.order.subtotal || 0)
                  - (feeEditing.order.discount_amount || 0)
                  + (Number(feeEditing.fee) || 0))
              )}</strong>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setFeeEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveFee} className="btn-primary inline-flex items-center gap-2"
                      data-testid="fee-save-btn">
                <Truck size={14} /> Save fee
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify customer modal */}
      {notifying && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setNotifying(null)} data-testid="notify-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 sm:p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-lg inline-flex items-center gap-2">
                <Send size={18} /> Notify customer
              </div>
              <button onClick={() => setNotifying(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              Sends order #{notifying.order.id.slice(0, 8)} confirmation + payment instructions to{" "}
              <strong>{notifying.order.customer_name}</strong>.
              Pick a channel below — the message is pre-filled with the updated total and how to pay.
            </p>
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3 mb-4">
              <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed text-zinc-700 max-h-48 overflow-y-auto">{notifying.message}</pre>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {notifying.whatsapp_url && (
                <a href={notifying.whatsapp_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1ebe5d]"
                   data-testid="notify-whatsapp">
                  <MessageCircle size={14} /> Send via WhatsApp
                </a>
              )}
              <a href={notifying.mailto}
                 className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-700"
                 data-testid="notify-email">
                <Mail size={14} /> Open in email
              </a>
            </div>
            <button
              onClick={() => { navigator.clipboard?.writeText(notifying.message); toast.success("Message copied"); }}
              className="mt-3 text-xs text-zinc-500 hover:text-zinc-900"
              data-testid="notify-copy">
              Copy message to clipboard
            </button>
          </div>
        </div>
      )}

      {/* Pickup date modal */}
      {pickupEditing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
             onClick={() => setPickupEditing(null)} data-testid="pickup-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-lg inline-flex items-center gap-2">
                <MapPin size={18} className="text-[#009A49]" /> Set pickup day
              </div>
              <button onClick={() => setPickupEditing(null)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-600 mb-4">
              Order <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">#{pickupEditing.order.id.slice(0, 8)}</code>
              {" "}for <strong>{pickupEditing.order.customer_name}</strong>. Pickup location: Greater Portmore, St. Catherine.
            </p>
            <label className="text-xs font-semibold text-zinc-600 block mb-1">Pickup day</label>
            <input type="date" value={pickupEditing.date}
                   onChange={(e) => setPickupEditing({ ...pickupEditing, date: e.target.value })}
                   className="input" data-testid="pickup-date-input" />
            <label className="text-xs font-semibold text-zinc-600 block mt-3 mb-1">Note to customer (optional)</label>
            <input value={pickupEditing.note}
                   onChange={(e) => setPickupEditing({ ...pickupEditing, note: e.target.value })}
                   className="input" placeholder="e.g. Between 3–6pm"
                   data-testid="pickup-note-input" />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPickupEditing(null)} className="btn-ghost">Cancel</button>
              <button onClick={savePickup} disabled={!pickupEditing.date}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="pickup-save-btn">
                <MapPin size={14} /> Save pickup day
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input { width:100%; border:1px solid #e4e4e7; border-radius:0.5rem; padding:0.5rem 0.75rem; font-size:0.875rem; }`}</style>
    </div>
  );
}

function F({ label, span = 1, children }) {
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <label className="text-xs font-semibold text-zinc-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}
